import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { Sparkles } from "lucide-react";

interface ProtectedAdminRouteProps {
  children: ReactNode;
}

export function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { toast } = useToast();
  const location = useLocation();
  const [hasShownToast, setHasShownToast] = useState(false);

  const isLoading = authLoading || adminLoading;

  useEffect(() => {
    // Show toast only once after loading is complete and user is authenticated but not admin
    if (!isLoading && user && !isAdmin && !hasShownToast) {
      setHasShownToast(true);
      toast({
        title: "Access Denied",
        description: "You don't have permission to access the admin area.",
        variant: "destructive",
      });
    }
  }, [isLoading, user, isAdmin, toast, hasShownToast]);

  // Reset toast flag when user changes
  useEffect(() => {
    setHasShownToast(false);
  }, [user?.id]);

  // Show elegant loading state while checking auth/admin status
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-charcoal text-gold">
        <Sparkles className="w-12 h-12 animate-pulse mb-6" />
        <div className="text-[10px] uppercase tracking-[0.5em] font-bold">Verifying Access...</div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Redirect to home if authenticated but not admin
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  // User is authenticated and is admin - render children
  return <>{children}</>;
}
